import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DynamicForm({ fields, onSubmit, submitLabel = "Avvia", initialValues = {} }) {
  const [values, setValues] = useState(() => {
    const init = {};
    fields.forEach((f) => {
      init[f.name] = initialValues[f.name] ?? (f.type === "checkbox" ? false : f.type === "file" ? null : "");
    });
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    fields.forEach((f) => {
      const v = values[f.name];
      if (f.required) {
        if (f.type === "file" && !v) errs[f.name] = "File obbligatorio";
        else if (f.type !== "file" && !v && v !== 0) errs[f.name] = "Campo obbligatorio";
      }
    });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="dynamic-form">
      {fields.map((f) => (
        <div key={f.name}>
          <label className="adr-label" htmlFor={`field-${f.name}`}>
            {f.label}
            {f.required && <span className="text-[#C8102E] ml-1">*</span>}
          </label>
          {f.type === "textarea" ? (
            <textarea
              id={`field-${f.name}`}
              className="adr-input min-h-[120px] resize-y"
              value={values[f.name] || ""}
              onChange={(e) => update(f.name, e.target.value)}
              data-testid={`field-${f.name}`}
            />
          ) : f.type === "select" ? (
            <select
              id={`field-${f.name}`}
              className="adr-input"
              value={values[f.name] || ""}
              onChange={(e) => update(f.name, e.target.value)}
              data-testid={`field-${f.name}`}
            >
              <option value="">Seleziona...</option>
              {(f.options || []).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : f.type === "number" ? (
            <input
              id={`field-${f.name}`}
              type="number"
              className="adr-input"
              value={values[f.name] ?? ""}
              onChange={(e) => update(f.name, e.target.value === "" ? "" : Number(e.target.value))}
              data-testid={`field-${f.name}`}
            />
          ) : f.type === "checkbox" ? (
            <input
              id={`field-${f.name}`}
              type="checkbox"
              className="w-5 h-5 rounded"
              checked={!!values[f.name]}
              onChange={(e) => update(f.name, e.target.checked)}
              data-testid={`field-${f.name}`}
            />
          ) : f.type === "file" ? (
            <FileField
              field={f}
              value={values[f.name]}
              onChange={(v) => update(f.name, v)}
            />
          ) : (
            <input
              id={`field-${f.name}`}
              type={f.type === "email" ? "email" : "text"}
              className="adr-input"
              value={values[f.name] || ""}
              onChange={(e) => update(f.name, e.target.value)}
              data-testid={`field-${f.name}`}
            />
          )}
          {errors[f.name] && (
            <div className="text-xs text-[#C8102E] mt-1">{errors[f.name]}</div>
          )}
          {f.help && <div className="text-xs text-[#555555] mt-1">{f.help}</div>}
        </div>
      ))}
      <div className="pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="adr-btn-primary"
          data-testid="submit-form-button"
        >
          {submitting ? "Avvio in corso..." : submitLabel}
        </button>
      </div>
    </form>
  );
}

function FileField({ field, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const accept = field.accept || {};
  const allowed = accept.types || [".pdf", ".docx", ".txt", ".csv", ".xlsx"];
  const maxMb = accept.max_mb || 25;

  const upload = async (file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (allowed.length && !allowed.includes(ext)) {
      toast.error(`Estensione non ammessa. Ammesse: ${allowed.join(", ")}`);
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Il file supera ${maxMb} MB`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ file_id: data.file_id, original_name: data.original_name, size: data.size });
      toast.success("File caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento fallito");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  if (value && value.file_id) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white" data-testid={`file-${field.name}-current`}>
        <FileText className="w-5 h-5 text-[#0F3D6B]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#1A1A1A] truncate">{value.original_name}</div>
          <div className="text-xs text-[#555555]">{(value.size / 1024).toFixed(1)} KB</div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[#555555] hover:text-[#C8102E] p-1.5 rounded"
          aria-label="Rimuovi file"
          data-testid={`file-${field.name}-remove`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`p-5 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${dragOver ? "border-[#0F3D6B] bg-[#F2F5F8]" : "border-[#E2E8F0] bg-white hover:bg-[#F2F5F8]"}`}
      data-testid={`file-${field.name}-dropzone`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={allowed.join(",")}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
          e.target.value = "";
        }}
        data-testid={`file-${field.name}-input`}
      />
      <div className="flex flex-col items-center gap-2 text-center">
        {uploading ? (
          <Loader2 className="w-6 h-6 text-[#0F3D6B] animate-spin" />
        ) : (
          <Upload className="w-6 h-6 text-[#0F3D6B]" />
        )}
        <div className="text-sm font-medium text-[#1A1A1A]">
          {uploading ? "Caricamento..." : "Trascina qui o clicca per caricare"}
        </div>
        <div className="text-xs text-[#555555]">
          {allowed.join(", ")} · max {maxMb} MB
        </div>
      </div>
    </div>
  );
}
